import React from 'react';
import HomeIcon from '@material-ui/icons/Home';
import {
  ApiBlueprint,
  AppRootElementBlueprint,
  createFrontendPlugin,
  createRouteRef,
  errorApiRef,
  identityApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  visitsApiRef,
  VisitsWebStorageApi,
  VisitListener,
  createCardExtension,
} from '@backstage/plugin-home';

// Old-system plugin needed to produce JSX-compatible card extension components
const widgetsPlugin = createPlugin({ id: 'home-widgets' });

const rootRouteRef = createRouteRef();

const HomePageExtension = PageBlueprint.make({
  params: {
    path: '/',
    title: 'Home',
    icon: React.createElement(HomeIcon),
    noHeader: true,
    routeRef: rootRouteRef,
    loader: async () => {
      const { HomePage } = await import('./HomePage');
      return React.createElement(HomePage);
    },
  },
});

const VisitsApiExtension = ApiBlueprint.make({
  name: 'visits',
  params: define =>
    define({
      api: visitsApiRef,
      deps: { identityApi: identityApiRef, errorApi: errorApiRef },
      factory: ({ identityApi, errorApi }) =>
        VisitsWebStorageApi.create({ identityApi, errorApi }),
    }),
});

const VisitListenerExtension = AppRootElementBlueprint.make({
  name: 'visit-listener',
  params: {
    element: React.createElement(VisitListener),
  },
});

export const GettingStartedCard = widgetsPlugin.provide(
  createCardExtension({
    name: 'GettingStartedCard',
    title: 'Getting Started',
    description: 'Tips and links to help you get started with Backstage',
    components: () =>
      import('./widgets').then(m => ({ Content: m.GettingStartedContent })),
    layout: { width: { defaultColumns: 4 }, height: { defaultRows: 6 } },
  }),
);

export const QuickLinksCard = widgetsPlugin.provide(
  createCardExtension({
    name: 'QuickLinksCard',
    title: 'Quick Links',
    description: 'Quick access to frequently used links',
    components: () =>
      import('./widgets').then(m => ({ Content: m.QuickLinksContent })),
    layout: { width: { defaultColumns: 4 }, height: { defaultRows: 3 } },
  }),
);

export const ToolkitCard = widgetsPlugin.provide(
  createCardExtension({
    name: 'ToolkitCard',
    title: 'Toolkit',
    description: 'A collection of useful links and tools',
    components: () =>
      import('./widgets').then(m => ({ Content: m.ToolkitContent })),
    layout: { width: { defaultColumns: 4 }, height: { defaultRows: 3 } },
  }),
);

export const WorldClocksCard = widgetsPlugin.provide(
  createCardExtension({
    name: 'WorldClocksCard',
    title: 'World Clocks',
    description: 'Displays clocks for configured time zones',
    components: () =>
      import('./widgets').then(m => ({ Content: m.WorldClocksContent })),
    layout: { width: { defaultColumns: 4 }, height: { defaultRows: 3 } },
  }),
);

export const SearchCard = widgetsPlugin.provide(
  createCardExtension({
    name: 'SearchCard',
    title: 'Search',
    description: 'A search bar that navigates to the search page on submit',
    components: () =>
      import('./widgets').then(m => ({ Content: m.SearchContent })),
    layout: { width: { defaultColumns: 4 }, height: { defaultRows: 1 } },
  }),
);

export const homePlugin = createFrontendPlugin({
  pluginId: 'home',
  routes: {
    root: rootRouteRef,
  },
  extensions: [HomePageExtension, VisitsApiExtension, VisitListenerExtension],
});
